/**
 * Push do app nativo (FCM no Android, APNs-via-FCM no iOS).
 *
 * Na web nada disso roda: o Web Push do PWA continua em pwa/pushClient.js, e os
 * dois canais convivem no backend (tabelas push_subscription e push_device).
 *
 * O token do FCM rotaciona sozinho — reinstalacao, restore de backup, limpeza
 * de dados. Por isso registramos a cada boot autenticado, e nao so uma vez.
 */

import api from '../api';
import { isNative, getPlatform, getPlugin } from './native';

const APP_VERSION = '1.0';

function plugin() {
  return getPlugin('PushNotifications');
}

export function disponivel() {
  return isNative() && !!plugin();
}

let ouvintesInstalados = false;
let ultimoTokenRegistrado = null;

async function registrarToken(token) {
  if (!token || token === ultimoTokenRegistrado) return;
  try {
    await api.post('/api/push/device', {
      device_token: token,
      platform: getPlatform(),
      app_version: APP_VERSION,
      device_model: (navigator.userAgent || '').slice(0, 100),
    });
    ultimoTokenRegistrado = token;
    console.log('[PWA] push-device-registrado', { plataforma: getPlatform() });
  } catch (e) {
    // 503 = servidor sem FCM configurado. Nao e erro do app.
    const status = e?.response?.status;
    console.log('[PWA] push-device-falhou', { status });
  }
}

function instalarOuvintes(p) {
  if (ouvintesInstalados) return;
  ouvintesInstalados = true;

  p.addListener('registration', (t) => registrarToken(t && t.value));

  p.addListener('registrationError', (e) => {
    console.log('[PWA] push-registration-error', { msg: e && e.error });
  });

  // App aberto: nao mostramos nada por cima da tela — o alerta ja aparece
  // dentro do app (alertas de estoque tem tela propria).
  p.addListener('pushNotificationReceived', (n) => {
    console.log('[PWA] push-recebido', { titulo: n && n.title });
  });

  // Toque na notificacao: leva para a tela certa. Quem navega e o App,
  // que tem o router — por isso o evento em vez de mexer em window.location
  // (que recarregaria o bundle inteiro).
  p.addListener('pushNotificationActionPerformed', (acao) => {
    const url = acao?.notification?.data?.url;
    if (url) window.dispatchEvent(new CustomEvent('push-navegar', { detail: url }));
  });
}

/**
 * Pede permissao e registra o device. Idempotente — pode chamar a cada boot.
 * Retorna 'ok' | 'negado' | 'indisponivel' | 'erro'.
 */
/**
 * Estado atual da permissao, sem pedir nada.
 *
 * Existe para a tela de notificacoes poder se descrever com honestidade no app:
 * o card original fala de navegador, service worker e "recarregue a pagina",
 * nada disso existe aqui.
 *
 * @returns {Promise<'indisponivel'|'concedida'|'negada'|'nao-pedida'>}
 */
export async function estado() {
  const p = plugin();
  if (!p) return 'indisponivel';
  try {
    const perm = await p.checkPermissions();
    if (perm.receive === 'granted') return 'concedida';
    if (perm.receive === 'denied') return 'negada';
    return 'nao-pedida';
  } catch {
    return 'indisponivel';
  }
}

export async function registrar() {
  const p = plugin();
  if (!p) return 'indisponivel';

  try {
    let perm = await p.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await p.requestPermissions();
    }
    if (perm.receive !== 'granted') return 'negado';

    // Canal do Android: aparece nas configuracoes do sistema com esse nome,
    // e a usuaria pode silenciar so ele em vez do app inteiro. O id tem que
    // bater com default_notification_channel_id do AndroidManifest.
    if (getPlatform() === 'android' && typeof p.createChannel === 'function') {
      try {
        await p.createChannel({
          id: 'alertas_estoque',
          name: 'Alertas de estoque',
          description: 'Estoque baixo e lotes perto do vencimento',
          importance: 4,       // HIGH: aparece como banner
          visibility: 1,       // PUBLIC
          vibration: true,
        });
      } catch { /* canal ja existe: segue */ }
    }

    instalarOuvintes(p);
    await p.register();   // dispara o listener 'registration' com o token
    return 'ok';
  } catch (e) {
    console.log('[PWA] push-registrar-erro', { msg: e && e.message });
    return 'erro';
  }
}

/** Remove o device do backend (logout). */
export async function desregistrar() {
  if (!ultimoTokenRegistrado) return;
  try {
    await api.delete('/api/push/device', { data: { device_token: ultimoTokenRegistrado } });
  } catch { /* melhor esforco: o backend limpa token morto no proximo envio */ }
  ultimoTokenRegistrado = null;
}
